import { RotateCcw } from "lucide-react";

import FilterBar from "../ui/FilterBar";
import FilterSelect from "../ui/FilterSelect";
import Button from "../ui/Button";
import { useAuthStore } from "../../store/authStore";
import {
  getBudgetUnits,
  getCountryByCode,
  getDefaultBudgetUnit,
  resolveCountryCode,
} from "../../data/countries";

const toUniqueFilterOptions = (items = []) => {
  return Array.from(
    new Set(
      items
        .map((item) => (typeof item === "string" ? item.trim() : item))
        .filter((item) => item && item !== "All"),
    ),
  )
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map((value) => ({ label: value, value }));
};

function PriceField({ placeholder, value, unit, units, onValueChange, onUnitChange }) {
  const unitOptions = Object.keys(units);
  const selectedUnit = unitOptions.includes(unit) ? unit : unitOptions[0];

  return (
    <div className="properties-price-field">
      <input
        type="number"
        className="filter-select properties-price-input"
        placeholder={placeholder}
        value={value}
        min="0"
        onChange={(e) => onValueChange(e.target.value)}
      />
      <select
        className="filter-select properties-price-unit"
        value={selectedUnit}
        onChange={(e) => onUnitChange(e.target.value)}
        aria-label={`${placeholder} unit`}
      >
        {unitOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function PropertiesFilters({
  filterAgent,
  setFilterAgent,
  filterArea,
  setFilterArea,
  filterCountry,
  setFilterCountry,
  minPrice,
  setMinPrice,
  minPriceUnit,
  setMinPriceUnit,
  maxPrice,
  setMaxPrice,
  maxPriceUnit,
  setMaxPriceUnit,
  agents = [],
  properties = [],
  onReset,
}) {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";
  const areaOptions = toUniqueFilterOptions(properties.map((p) => p.area));
  const countryOptions = Array.from(
    new Set(properties.map((property) => resolveCountryCode(property))),
  )
    .sort()
    .map((code) => ({
      value: code,
      label: getCountryByCode(code)?.name || code,
    }));

  const unitCountryCode = filterCountry === "All" ? "IN" : filterCountry;
  const priceUnits = getBudgetUnits(unitCountryCode);

  const hasActiveFilters =
    (isAdmin && filterAgent !== "All") ||
    filterArea !== "All" ||
    filterCountry !== "All" ||
    minPrice !== "" ||
    maxPrice !== "";

  const syncUnitsForCountry = (countryCode) => {
    const nextUnits = Object.keys(getBudgetUnits(countryCode));
    const fallback = getDefaultBudgetUnit(countryCode);
    if (!nextUnits.includes(minPriceUnit)) setMinPriceUnit(fallback);
    if (!nextUnits.includes(maxPriceUnit)) setMaxPriceUnit(fallback);
  };

  return (
    <FilterBar className="properties-filters">
      <div className="properties-filters-fields">
        {isAdmin && (
          <FilterSelect
            label="Agent"
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            options={[{ label: "All Agents", value: "All" }, ...agents]}
          />
        )}

        <FilterSelect
          label="Area"
          value={filterArea}
          onChange={(e) => setFilterArea(e.target.value)}
          options={[{ label: "All Areas", value: "All" }, ...areaOptions]}
        />

        <FilterSelect
          label="Country"
          value={filterCountry}
          onChange={(e) => {
            const nextCountry = e.target.value;
            setFilterCountry(nextCountry);
            syncUnitsForCountry(nextCountry === "All" ? "IN" : nextCountry);
          }}
          options={[
            { label: "All Countries", value: "All" },
            ...countryOptions,
          ]}
        />

        <div className="filter-item properties-price-range">
          <span className="filter-label">Price Range</span>
          <div className="properties-price-range-row">
            <PriceField
              placeholder="Min"
              value={minPrice}
              unit={minPriceUnit}
              units={priceUnits}
              onValueChange={setMinPrice}
              onUnitChange={setMinPriceUnit}
            />
            <span className="properties-price-sep">–</span>
            <PriceField
              placeholder="Max"
              value={maxPrice}
              unit={maxPriceUnit}
              units={priceUnits}
              onValueChange={setMaxPrice}
              onUnitChange={setMaxPriceUnit}
            />
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="properties-filter-reset"
        onClick={onReset}
        disabled={!hasActiveFilters}
      >
        <RotateCcw size={15} />
        Reset
      </Button>
    </FilterBar>
  );
}
